/** Copyright © 2013-2022 DataYes, All Rights Reserved. */

import React from 'react';
import { Form, Input, Checkbox, Button, message } from 'antd';
import { MobileOutlined, SafetyOutlined } from '@ant-design/icons';
import { encrypt } from '../utils/encrypt';
import { stringify } from '../utils/qs';
import API from '../services';
import CaptchaSlide from '../components/slider';
import CountdownButton from '../components/count-down';
import Header from '../components/header';
import { StyledRegister } from './style';

const FormItem = Form.Item;

const noop = () => {};

type OwnRegisterProps = {
  onClose?: (...args: any[]) => any;
  form?: any;
  afterRegister?: (...args: any[]) => any;
  changeView: (...args: any[]) => any;
  isBindMobile?: boolean;
  unionId?: string;
};

type RegisterState = any;

type RegisterProps = OwnRegisterProps & typeof Register.defaultProps;

class Register extends React.PureComponent<RegisterProps, RegisterState> {
  static defaultProps = {
    onClose: noop,
    afterRegister: noop,
  };

  captchaRef: any;
  codeRef: any;
  sendTime: any;

  state = {
    submitting: false,
  };

  constructor(props: RegisterProps) {
    super(props);
    this.sendTime = undefined;
  }

  handleSendCode = () => {
    const { form } = this.props;

    // 再次点击“发送验证码”，先清空原输入信息
    form.setFieldsValue({
      code: '',
    });

    form.validateFields(['mobile', 'position'], (err: any, values: any) => {
      if (!err) {
        API.sendSms(
          stringify({
            mobile: values.mobile,
            offset: values.position,
            source: 'rrp',
          })
        ).then((response: any) => {
          if (response.code === 0 && response.content !== false) {
            message.success('短信验证码已发送成功');
            this.codeRef.startCountdown();
            this.sendTime = new Date().getTime();
            // pingback.send({
            //   eventId: 801,
            //   eventName: "短信验证码已发送成功"
            // });
          } else {
            message.success('短信验证码发送失败');
            this.captchaRef.reset();
            this.sendTime = undefined;
          }
        });
      }
    });
  };

  setCodeRef = (ref: any) => {
    this.codeRef = ref;
  };

  setCaptchaRef = (ref: any) => {
    this.captchaRef = ref;
  };

  handleSubmit = (e: any) => {
    e.preventDefault();
    const { form, unionId, afterRegister } = this.props;
    const values = form.getFieldsValue();

    if (!values.agree) {
      message.warn('您未同意《通联数据用户协议》');
    }

    form.validateFields((err: any) => {
      if (!err) {
        this.setState({ submitting: true });

        const params = {
          username: encrypt(values.mobile),
          password: encrypt(values.code),
          isSlide: true,
          identityType: 'MOBILE_SMS',
          unionId,
        };

        API.register(stringify(params)).then((response: any) => {
          this.setState({ submitting: false });
          if (response.code !== 0) {
            this.captchaRef.reset();
          }

          switch (response.code) {
            case 0:
              if (response.content.result === 'SUCCESS') {
                if (typeof afterRegister === 'function') {
                  afterRegister();
                }
              } else {
                form.setFields([
                  {
                    name: 'code',
                    errors: '请输入正确的短信验证码',
                  },
                ]);
              }
              break;
            case 17:
              form.setFields([
                {
                  name: 'mobile',
                  value: values.mobile,
                  errors: '手机号码已注册',
                },
              ]);
              break;
            // case 17:
            //   form.setFields({
            //     mobile: {
            //       value: values.mobile,
            //       errors: [new Error('手机验证码发送次数已达上限')],
            //     },
            //   })
            //   break;
            default:
              form.setFields([
                {
                  name: 'code',
                  errors: '请输入正确的短信验证码',
                },
              ]);
          }
        });
      }
    });
  };

  handleLoginClick = (e: any) => {
    e.preventDefault();

    this.props.changeView('login');
  };

  handleCodeChange = (e: any) => {
    const value = e.target.value;

    if (/^\d{6}$/.test(value) && this.sendTime) {
      // pingback.send({
      //   eventId: 802,
      //   eventName: "短信送达时间",
      //   receivedTime: new Date().getTime() - this.sendTime
      // });
      this.sendTime = undefined;
    }
  };

  render() {
    const { isBindMobile } = this.props;

    return (
      <StyledRegister>
        <Header onClose={this.props.onClose} />
        <div className="dyc-register-main dyc-login-register-main">
          <h2>{isBindMobile ? '绑定手机号' : '欢迎注册萝卜投研'}</h2>
          <Form onFinish={this.handleSubmit}>
            <FormItem
              name="mobile"
              rules={[
                {
                  required: true,
                  message: '请输入手机号码',
                },
                {
                  pattern: /^((13[0-9])|(14[0-9])|(15[0-9])|(16[0-9])|(17[0-9])|(18[0-9])|(19[0-9]))\d{8}$/,
                  message: '手机号码格式错误',
                },
              ]}
            >
              <Input prefix={<MobileOutlined />} placeholder="手机号码（仅支持中国大陆手机）" />
            </FormItem>
            <FormItem name="position" rules={[{ required: true, message: '请向右滑动完成拼图' }]}>
              <CaptchaSlide ref={this.setCaptchaRef} />
            </FormItem>
            <FormItem
              className="form-item-sms"
              name="code"
              rules={[
                { required: true, message: '请输入短信验证码' },
                {
                  pattern: /^\d{6}$/,
                  message: '短信验证码格式错误',
                },
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="短信验证码"
                onChange={this.handleCodeChange}
                suffix={<CountdownButton ref={this.setCodeRef} onClick={this.handleSendCode} />}
              />
            </FormItem>
            <FormItem
              className="form-submit-wrapper"
              name="agree"
              valuePropName="checked"
              initialValue
            >
              <Checkbox>我同意</Checkbox>
              <a href="https://www.datayes.com/protocol.html" target="_blank">
                《通联数据用户协议》
              </a>
              <Button
                className="form-submit-button"
                type="primary"
                htmlType="submit"
                loading={this.state.submitting}
              >
                {isBindMobile ? '提交' : '注册萝卜投研个人账号'}
              </Button>
            </FormItem>
          </Form>
          {!isBindMobile && (
            <div className="dyc-register-footer">
              <span>
                已有账号？
                {/*  eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="" onClick={this.handleLoginClick}>
                  直接登录
                </a>
              </span>
              <a href="https://roboams.datayes.com/auth/apply" target="_blank">
                注册为萝卜投资机构用户
              </a>
            </div>
          )}
        </div>
      </StyledRegister>
    );
  }
}

export default Register;
